import { selectValue } from "../utils";

type QuestionLike = { options?: ({ isCorrect?: boolean | null } | null)[] | null };

export function correctIndex(question: QuestionLike): number {
  const opts = question.options ?? [];
  return opts.findIndex((o) => Boolean(o?.isCorrect));
}

export function computeScore(questions: QuestionLike[], answers: (number | null)[]): number {
  return questions.reduce((score, question, i) => {
    const correct = correctIndex(question);
    return correct >= 0 && answers[i] === correct ? score + 1 : score;
  }, 0);
}

// WPGraphQL for ACF returns null for a number field stored as 0, so the required
// minScore-0 floor tier always arrives as null — `?? 0` restores its floor semantics.
export function resolveResultTier<T extends { minScore?: number | null }>(
  levels: T[],
  score: number,
): T | null {
  const sorted = [...levels].sort((a, b) => (b.minScore ?? 0) - (a.minScore ?? 0));
  return sorted.find((lvl) => score >= (lvl.minScore ?? 0)) ?? null;
}

export function progressLabel(index: number, total: number): string {
  return `${Math.min(index + 1, total)} / ${total}`;
}

export function progressPct(index: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round(((index + 1) / total) * 100);
}

type PersonalityQuestionLike = { options?: ({ resultKey?: string | (string | null)[] | null } | null)[] | null };

// ACF selects arrive as arrays while text fields arrive as strings, so both sides
// must be normalized. Votes are counted only for keys that still exist in `results`,
// so a renamed or deleted result can never win. Ties resolve to the first-defined
// result because the reduce walks `results` in author order and only replaces on a strict >.
export function tallyPersonality<T extends { resultKey?: string | (string | null)[] | null }>(
  questions: PersonalityQuestionLike[],
  answers: (number | null)[],
  results: T[],
): T | null {
  const votes = new Map<string, number>();

  questions.forEach((question, i) => {
    const answer = answers[i];
    if (answer == null) return;
    const key = selectValue((question.options ?? [])[answer]?.resultKey);
    if (!key) return;
    votes.set(key, (votes.get(key) ?? 0) + 1);
  });

  return results.reduce<{ winner: T | null; count: number }>(
    (best, result) => {
      const resultKey = selectValue(result.resultKey);
      const count = resultKey ? votes.get(resultKey) ?? 0 : 0;
      return count > best.count ? { winner: result, count } : best;
    },
    { winner: null, count: 0 },
  ).winner;
}
